import React, { Fragment, lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { selectPerms } from './components/core/auth/authSlice';
import { Routes as NavRoutes } from './components/nav/Routes';
import { profileMenu } from './components/nav/Routes';
import { Fallback } from './components/shared';
import AktivuLoader from './components/shared/AktivuLoader';
import theme from './themes/theme';
import { getAllowedRoutes } from './utils/utils';

const GetCode = lazy(() => import('./components/core/auth/GetCode'));
const Login = lazy(() => import('./components/core/auth/Login'));
const PasswordReset = lazy(() =>
  import('./components/core/auth/PasswordReset')
);
const Registration = lazy(() =>
  import('./components/core/auth/Registration')
);
const RequireAuth = lazy(() =>
  import('./components/core/auth/RequireAuth')
);
const RootLogin = lazy(() =>
  import('./components/core/auth/RootLogin')
);
const VerifyCode = lazy(() =>
  import('./components/core/auth/VerifyCode')
);
const AktivuSnackbar = lazy(() =>
  import('./components/shared/AktivuSnackbar/AktivuSnackbar')
);
const Landing = lazy(() => import('./layouts/Landing'));

/**
 * global error handler for the ErrorBoundary
 * Exmple Usage in Aktivu Components:
 *  export const SomeAktivuComponent = () => {
 *    try {
 *      ...
 *      ...
 *    } catch (e) {
 *      # Error boundary shall catch these errors
 *      throw new Error(e)
 *    }
 *
 * }
 * @param {*} error
 * @param {*} errorInfo
 */
const errorHandler = (error, errorInfo) => {
  console.error(`Error ${error}: ${errorInfo}`);
};

const App = () => {
  const location = useLocation();
  const perms = useSelector(selectPerms);
  let managePerms = [];
  let readPerms = [];
  let allPerms = [];

  if (perms?.M) {
    managePerms = perms.M;
  }
  if (perms?.R) {
    readPerms = perms.R;
  }

  allPerms = [...new Set([...managePerms, ...readPerms])];
  const allowedRoutes =
    getAllowedRoutes(allPerms, NavRoutes, perms?.RN) || [];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary
        FallbackComponent={Fallback}
        onError={errorHandler}
        key={location.pathname}
      >
        <Suspense fallback={<AktivuLoader />}>
          <AktivuSnackbar />
          <Routes>
            {/* public routes */}
            <Route path="login" element={<RootLogin />} />
            <Route path=":orgName/login" element={<Login />} />
            <Route path="get-code" element={<GetCode />} />
            <Route path="verify" element={<VerifyCode />} />
            <Route
              path="password-reset"
              element={<PasswordReset />}
            />
            <Route path="registration" element={<Registration />} />

            {/* protected routes */}
            <Route element={<RequireAuth />}>
              <Route path="aktivu" element={<Landing />}>
                {allowedRoutes.map((r, i) =>
                  r?.type === 'item' ? (
                    <Route
                      path={r.path}
                      key={`route-${i}`}
                      element={r.main()}
                    ></Route>
                  ) : (
                    <Fragment key={`child-route-${i}`}>
                      {r.children.map((c, ci) => (
                        <Route
                          path={c.path}
                          key={`route-child-${ci}`}
                          element={c.main()}
                        ></Route>
                      ))}
                    </Fragment>
                  )
                )}
                {profileMenu.map((r, i) =>
                  r?.type === 'item' ? (
                    <Route
                      path={r.path}
                      key={`route-${i}`}
                      element={r.main()}
                    ></Route>
                  ) : (
                    <Fragment key={`child-route-${i}`}>
                      {r.children.map((c, ci) => (
                        <Route
                          path={c.path}
                          key={`route-child-${ci}`}
                          element={c.main()}
                        ></Route>
                      ))}
                    </Fragment>
                  )
                )}
              </Route>
              {/* <Route path="devicekey" element={<DeviceKey />}></Route> */}
              {/* Catch all - replace with 404 component if you want */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
